async function getPostArticle(postUrl) {
    const response = await fetch(postUrl, {
        headers: {
            'X-Requested-With': 'portfolio-modal'
        }
    });

    if (!response.ok) {
        throw new Error(`Unable to load ${postUrl}`);
    }

    const html = await response.text();
    const documentHtml = new DOMParser().parseFromString(html, 'text/html');
    const article = documentHtml.querySelector('.gh-article');

    if (!article) {
        throw new Error('Post content was not found');
    }

    return article.outerHTML;
}

function escapeHtml(value) {
    return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#39;');
}

function getFocusableElements(container) {
    const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(container.querySelectorAll(selector)).filter((element) => !element.hasAttribute('disabled'));
}

function getFilterFromHash() {
    if (!window.location.hash.startsWith('#tag/')) {
        return 'all';
    }

    return window.location.hash.replace('#tag/', '').trim() || 'all';
}

function updateHash(filterValue) {
    const nextHash = filterValue === 'all' ? '' : `#tag/${filterValue}`;
    const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
    window.history.replaceState(null, '', nextUrl);
}

function isElement(value) {
    return value instanceof Element;
}

function parseExcerptMetadata(rawExcerpt, fallbackYear) {
    const metadata = {
        year: fallbackYear || '',
        medium: '',
        dimensions: '',
        locationLabel: '',
        locationUrl: ''
    };

    if (!rawExcerpt) {
        return {
            metadata,
            summary: ''
        };
    }

    const segments = rawExcerpt.split('|').map((segment) => segment.trim()).filter(Boolean);
    const summaryParts = [];
    const usedIndexes = new Set();

    const applyValue = (key, value) => {
        const cleanedValue = value.trim();

        if (!cleanedValue) {
            return;
        }

        if (key === 'year') {
            metadata.year = cleanedValue;
        } else if (key === 'medium') {
            metadata.medium = cleanedValue;
        } else if (key === 'dimensions') {
            metadata.dimensions = cleanedValue;
        } else if (key === 'location') {
            metadata.locationLabel = cleanedValue;
        } else if (key === 'link') {
            metadata.locationUrl = cleanedValue;
        }
    };

    segments.forEach((segment, index) => {
        const match = segment.match(/^(year|medium|dimensions|location|link)\s*[:=]\s*(.+)$/i);

        if (match) {
            usedIndexes.add(index);
            applyValue(match[1].toLowerCase(), match[2]);
        }
    });

    segments.forEach((segment, index) => {
        if (!usedIndexes.has(index)) {
            summaryParts.push(segment);
        }
    });

    if (metadata.locationUrl && !metadata.locationLabel) {
        metadata.locationLabel = 'Project link';
    }

    return {
        metadata,
        summary: summaryParts.join(' | ')
    };
}

function renderMetadataBlock(metadata) {
    const rows = [];

    if (metadata.year) {
        rows.push(`<li><span>Year</span><strong>${escapeHtml(metadata.year)}</strong></li>`);
    }

    if (metadata.medium) {
        rows.push(`<li><span>Medium</span><strong>${escapeHtml(metadata.medium)}</strong></li>`);
    }

    if (metadata.dimensions) {
        rows.push(`<li><span>Dimensions</span><strong>${escapeHtml(metadata.dimensions)}</strong></li>`);
    }

    if (metadata.locationLabel) {
        const label = escapeHtml(metadata.locationLabel);

        if (metadata.locationUrl) {
            rows.push(`<li><span>Location</span><strong><a href="${escapeHtml(metadata.locationUrl)}" target="_blank" rel="noopener">${label}</a></strong></li>`);
        } else {
            rows.push(`<li><span>Location</span><strong>${label}</strong></li>`);
        }
    }

    if (!rows.length) {
        return '';
    }

    return `<ul class="portfolio-meta-list">${rows.join('')}</ul>`;
}

export default function portfolioModal() {
    const modal = document.querySelector('#portfolio-modal');
    const lightbox = document.querySelector('#portfolio-lightbox');
    const modalContent = document.querySelector('[data-portfolio-content]');
    const modalScroll = document.querySelector('.portfolio-modal-scroll');
    const closeButton = document.querySelector('[data-portfolio-close][type="button"]');
    const previousButton = document.querySelector('[data-portfolio-prev]');
    const nextButton = document.querySelector('[data-portfolio-next]');
    const filterMeta = document.querySelector('[data-filter-meta]');
    const emptyState = document.querySelector('[data-portfolio-empty]');
    const filterResetButton = document.querySelector('[data-filter-reset]');
    const filterButtons = Array.from(document.querySelectorAll('[data-filter-button]'));
    
    const lightboxImage = document.querySelector('.portfolio-lightbox-image');
    const lightboxLink = document.querySelector('.portfolio-lightbox-link');

    if (!modal || !modalContent) {
        return;
    }

    if (!modal || !modalContent) {
        return;
    }

    const cardElements = Array.from(document.querySelectorAll('[data-portfolio-card]'));
    const triggerElements = Array.from(document.querySelectorAll('[data-portfolio-trigger]'));
    const allowedFilters = new Set(filterButtons.map((button) => button.getAttribute('data-filter-value') || 'all'));
    allowedFilters.add('all');
    const imageProtectionEnabled = document.body.classList.contains('portfolio-protect');

    let visibleTriggers = triggerElements;
    let currentIndex = -1;
    let currentTrigger = null;
    let currentFilter = 'all';
    let lastFocusedElement = null;
    let touchStartX = 0;
    let touchStartY = 0;

    const openLightbox = (imageSrc, altText) => {
        if (!lightbox || !lightboxImage || !lightboxLink) return;

        lightboxImage.src = imageSrc;
        lightboxImage.alt = altText;
        lightboxLink.href = imageSrc;

        lightbox.showModal();
        lightbox.classList.add('is-open');
    };

    const closeLightbox = () => {
        if (lightbox) {
            lightbox.classList.remove('is-open');
            lightbox.close();
        }
    };

    const applyImageProtection = (rootElement) => {
        if (!imageProtectionEnabled || !rootElement || typeof rootElement.querySelectorAll !== 'function') {
            return;
        }

        rootElement.querySelectorAll('img').forEach((image) => {
            image.classList.add('is-protected-image');
            image.setAttribute('draggable', 'false');
        });
    };

    const updateVisibleTriggers = () => {
        visibleTriggers = triggerElements.filter((trigger) => {
            const card = trigger.closest('[data-portfolio-card]');
            return card && !card.hidden;
        });

        if (currentTrigger) {
            currentIndex = visibleTriggers.indexOf(currentTrigger);
        }

        if (currentIndex >= visibleTriggers.length) {
            currentIndex = visibleTriggers.length - 1;
        }

        const hasPrevious = currentIndex > 0;
        const hasNext = currentIndex >= 0 && currentIndex < visibleTriggers.length - 1;

        if (previousButton) {
            previousButton.disabled = !hasPrevious;
        }

        if (nextButton) {
            nextButton.disabled = !hasNext;
        }
    };

    const updateFilterMeta = () => {
        if (filterMeta) {
            const label = currentFilter === 'all' ? 'All projects' : `${currentFilter.replaceAll('-', ' ')} projects`;
            const quantity = `${visibleTriggers.length} ${visibleTriggers.length === 1 ? 'item' : 'items'}`;
            filterMeta.textContent = `${label} - ${quantity}`;
        }

        if (emptyState) {
            emptyState.hidden = visibleTriggers.length > 0;
        }
    };

    const closeModal = () => {
        if (modal.open) {
            modal.classList.remove('is-open');
            modal.close();
        }

        currentIndex = -1;
        currentTrigger = null;
        document.body.classList.remove('overflow-hidden');
    };

    const openModal = (trigger = null) => {
        if (typeof modal.showModal !== 'function') return;

        modal.showModal();
        modal.classList.add('is-open');
        document.body.classList.add('overflow-hidden');

        if (closeButton) {
            closeButton.focus();
        }
    };


    const injectMetadataIntoModal = (metadata) => {
        const metadataHtml = renderMetadataBlock(metadata);

        if (!metadataHtml) {
            return;
        }

        const article = modalContent.querySelector('.gh-article');
        const header = article?.querySelector('header');

        if (!article) {
            return;
        }

        const wrapper = document.createElement('div');
        wrapper.className = 'portfolio-meta';
        wrapper.innerHTML = metadataHtml;

        if (header) {
            header.append(wrapper);
        } else {
            article.prepend(wrapper);
        }
    };

    const loadTrigger = async (trigger) => {
        const postUrl = trigger.getAttribute('data-post-url') || '';
        const postTitle = escapeHtml(trigger.getAttribute('data-post-title') || 'Artwork');
        const rawExcerpt = trigger.getAttribute('data-post-excerpt') || '';
        const postYear = trigger.getAttribute('data-post-year') || '';
        const { metadata, summary } = parseExcerptMetadata(rawExcerpt, postYear);
        const metadataHtml = renderMetadataBlock(metadata);
        const summaryHtml = summary ? `<p class="mt-3 text-neutral/70">${escapeHtml(summary)}</p>` : '';

        currentTrigger = trigger;
        currentIndex = visibleTriggers.indexOf(trigger);

        modalContent.innerHTML = `
            <article class="gh-article">
                <header class="mb-6 border-b border-base-300 pb-6">
                    <div class="portfolio-skeleton portfolio-skeleton-title"></div>
                    <div class="portfolio-skeleton portfolio-skeleton-text" style="width: 30%"></div>
                    <div class="portfolio-skeleton portfolio-skeleton-text" style="width: 20%"></div>
                </header>
                <div class="portfolio-skeleton-content">
                    <div class="portfolio-skeleton portfolio-skeleton-line"></div>
                    <div class="portfolio-skeleton portfolio-skeleton-line"></div>
                    <div class="portfolio-skeleton portfolio-skeleton-line"></div>
                    <div class="portfolio-skeleton portfolio-skeleton-line short"></div>
                </div>
            </article>
        `;

        openModal(trigger);
        updateVisibleTriggers();

        try {
            const articleHtml = await getPostArticle(postUrl);
            console.log('Modal content loaded successfully');
            modalContent.innerHTML = articleHtml;
            injectMetadataIntoModal(metadata);
            applyImageProtection(modalContent);
        } catch (error) {
            console.error('Modal load error:', error);
            modalContent.innerHTML = `
                <article class="gh-article">
                    <h2 class="font-display text-4xl text-neutral">${postTitle}</h2>
                    <p class="mt-4 text-neutral/70">The project could not be loaded right now. Try opening it directly.</p>
                    <a class="btn btn-primary mt-6" href="${escapeHtml(postUrl)}">Open project page</a>
                </article>
            `;
        }
    };

    const moveModal = (delta) => {
        if (!modal.open || !visibleTriggers.length || currentIndex < 0) {
            return;
        }

        const nextIndex = currentIndex + delta;

        if (nextIndex < 0 || nextIndex >= visibleTriggers.length) {
            return;
        }

        loadTrigger(visibleTriggers[nextIndex]);
    };

    const setActiveFilter = async (filterValue, syncHash = true) => {
        currentFilter = allowedFilters.has(filterValue) ? filterValue : 'all';

        filterButtons.forEach((button) => {
            const isActive = button.getAttribute('data-filter-value') === currentFilter;
            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        cardElements.forEach((card) => {
            if (currentFilter === 'all') {
                card.hidden = false;
                return;
            }

            const cardTags = (card.getAttribute('data-tags') || '').split(' ').filter(Boolean);
            card.hidden = !cardTags.includes(currentFilter);
        });

        updateVisibleTriggers();
        updateFilterMeta();

        if (syncHash && filterButtons.length) {
            updateHash(currentFilter);
        }

        if (modal.open && currentTrigger && !visibleTriggers.includes(currentTrigger)) {
            if (!visibleTriggers.length) {
                closeModal();
                return;
            }

            await loadTrigger(visibleTriggers[0]);
        }
    };

    updateVisibleTriggers();
    applyImageProtection(document.documentElement);

    if (imageProtectionEnabled) {
        document.addEventListener('contextmenu', (event) => {
            if (isElement(event.target) && event.target.closest('.is-protected-image')) {
                event.preventDefault();
            }
        });

        document.addEventListener('dragstart', (event) => {
            if (isElement(event.target) && event.target.closest('.is-protected-image')) {
                event.preventDefault();
            }
        });

        document.addEventListener('selectstart', (event) => {
            if (isElement(event.target) && event.target.closest('.is-protected-image')) {
                event.preventDefault();
            }
        });
    }

    filterButtons.forEach((button) => {
        button.addEventListener('click', async () => {
            const filterValue = button.getAttribute('data-filter-value') || 'all';
            await setActiveFilter(filterValue);
        });
    });

    if (filterResetButton) {
        filterResetButton.addEventListener('click', async () => {
            await setActiveFilter('all');
        });
    }

    if (filterButtons.length) {
        window.addEventListener('hashchange', async () => {
            await setActiveFilter(getFilterFromHash(), false);
        });
    }

    document.addEventListener('click', async (event) => {
        const trigger = event.target.closest('[data-portfolio-trigger]');

        if (trigger) {
            event.preventDefault();
            lastFocusedElement = trigger;
            await loadTrigger(trigger);
            return;
        }

        if (event.target.closest('[data-portfolio-close]')) {
            closeModal();
        }

        if (event.target.closest('[data-lightbox-close]')) {
            closeLightbox();
        }
    });

    if (previousButton) {
        previousButton.addEventListener('click', () => {
            moveModal(-1);
        });
    }

    if (nextButton) {
        nextButton.addEventListener('click', () => {
            moveModal(1);
        });
    }

    if (modalScroll) {
        modalScroll.addEventListener('touchstart', (event) => {
            const touch = event.changedTouches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
        }, { passive: true });

        modalScroll.addEventListener('touchend', (event) => {
            if (!modal.open) {
                return;
            }

            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;

            if (Math.abs(deltaX) < 50 || Math.abs(deltaY) > 70) {
                return;
            }

            if (deltaX < 0) {
                moveModal(1);
            } else {
                moveModal(-1);
            }
        }, { passive: true });
    }

    document.addEventListener('keydown', (event) => {
        if (!modal.open) {
            return;
        }

        if (event.key === 'Escape') {
            closeModal();
            return;
        }

        if (event.key === 'ArrowRight') {
            event.preventDefault();
            moveModal(1);
            return;
        }

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            moveModal(-1);
            return;
        }

        if (event.key === 'Tab') {
            const focusableElements = getFocusableElements(modal);

            if (!focusableElements.length) {
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    });

    modal.addEventListener('close', () => {
        modal.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');

        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
            lastFocusedElement.focus();
        }
    });

    setActiveFilter(filterButtons.length ? getFilterFromHash() : 'all', false);
}
